import { useState } from 'react';

interface AgreementSectionProps {
  type: 'patient' | 'caregiver';
  privacyChecked: boolean;
  thirdPartyChecked: boolean;
  onPrivacyChange: (checked: boolean) => void;
  onThirdPartyChange: (checked: boolean) => void;
  disabled?: boolean;
}

export function getPrivacyContent(type: 'patient' | 'caregiver'): { title: string; content: string } {
  if (type === 'patient') {
    return {
      title: '개인정보 수집 및 이용 동의 (환자/보호자)',
      content: `코리아케어러스는 환자 등록 및 간병 매칭 서비스 제공을 위해 아래와 같이 개인정보를 수집 및 이용합니다.

1. 수집하는 개인정보 항목
- 필수: 간병 장소(집/병원), 주소 또는 병원명, 대소변 도움 필요 여부, 간병 필요 지역, 간병 시작일, 간병 필요 기간, 희망 간병비
- 선택: 병명, 환자 상태, 키, 체중, 체질, 간병 방식, 간병인 선호 성별, 추가 요청사항

2. 수집 및 이용 목적
- 간병인-환자 매칭 서비스 제공
- 환자 상태에 적합한 간병인 추천 및 배정
- 매칭 성사 시 간병인에게 환자 정보(병명, 상태, 필요 케어 범위) 제공
- 서비스 관련 고지사항 전달

3. 보유 및 이용 기간
- 회원 탈퇴 시까지 보유
- 단, 관련 법령에 따라 일정 기간 보관이 필요한 경우 해당 기간 동안 보관

4. 동의를 거부할 권리 및 거부 시 불이익
- 귀하는 개인정보 수집 및 이용에 대한 동의를 거부할 권리가 있습니다.
- 다만, 필수 항목에 대한 동의를 거부하실 경우 환자 등록 및 간병 매칭 서비스 이용이 불가능합니다.`,
    };
  }
  return {
    title: '개인정보 수집 및 이용 동의 (간병인)',
    content: `코리아케어러스는 간병인 등록 및 매칭 서비스 제공을 위해 아래와 같이 개인정보를 수집 및 이용합니다.

1. 수집하는 개인정보 항목
- 필수: 간병 경력, 희망 시급, 간병 가능 지역, 간병 가능 요일, 간병 가능 시간대, 빠른 시작일
- 선택: 보유 자격증, 자기소개

2. 수집 및 이용 목적
- 간병인-환자 매칭 서비스 제공
- 환자/보호자에게 간병인 프로필(경력, 자격증, 가능 지역/시간) 제공
- 매칭 성사 시 환자/보호자에게 간병인 연락처 제공
- 서비스 관련 고지사항 전달

3. 보유 및 이용 기간
- 회원 탈퇴 시까지 보유
- 단, 관련 법령에 따라 일정 기간 보관이 필요한 경우 해당 기간 동안 보관

4. 동의를 거부할 권리 및 거부 시 불이익
- 귀하는 개인정보 수집 및 이용에 대한 동의를 거부할 권리가 있습니다.
- 다만, 필수 항목에 대한 동의를 거부하실 경우 간병인 등록 및 매칭 서비스 이용이 불가능합니다.`,
  };
}

export function getThirdPartyContent(type: 'patient' | 'caregiver'): { title: string; content: string } {
  if (type === 'patient') {
    return {
      title: '개인정보 제3자 제공 동의 (환자/보호자)',
      content: `코리아케어러스는 매칭 서비스 제공을 위해 아래와 같이 개인정보를 제3자(간병인)에게 제공합니다.

1. 제공받는 자: 매칭된 간병인
2. 제공 목적: 간병 서비스 제공을 위한 환자 상태 파악 및 원활한 간병 진행
3. 제공 항목: 병명, 환자 상태, 대소변 도움 필요 여부, 간병 장소(주소 또는 병원명), 간병 방식, 추가 요청사항
4. 제공 시점: 양측이 매칭을 수락한 시점
5. 보유 기간: 매칭 종료 후 30일

※ 매칭 성사 후 간병인은 환자 상태와 필요 정보를 확인하여 적절한 간병 서비스를 제공합니다.
※ 동의를 거부하실 경우 매칭 서비스 이용이 불가능합니다.`,
    };
  }
  return {
    title: '개인정보 제3자 제공 동의 (간병인)',
    content: `코리아케어러스는 매칭 서비스 제공을 위해 아래와 같이 개인정보를 제3자(환자/보호자)에게 제공합니다.

1. 제공받는 자: 매칭된 환자/보호자
2. 제공 목적: 환자/보호자가 간병인 선택 시 참고할 수 있도록 정보 제공
3. 제공 항목: 간병 경력, 보유 자격증, 간병 가능 지역/요일/시간대, 자기소개
4. 제공 시점: 양측이 매칭을 수락한 시점
5. 보유 기간: 매칭 종료 후 30일

※ 매칭 성사 후 환자/보호자는 간병인 프로필을 확인하여 매칭을 결정합니다.
※ 동의를 거부하실 경우 매칭 서비스 이용이 불가능합니다.`,
  };
}

export default function AgreementSection({
  type,
  privacyChecked,
  thirdPartyChecked,
  onPrivacyChange,
  onThirdPartyChange,
  disabled,
}: AgreementSectionProps) {
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null);

  const privacy = getPrivacyContent(type);
  const thirdParty = getThirdPartyContent(type);

  const items = [
    {
      key: 'privacy',
      label: '[필수] 개인정보 수집 및 이용 동의',
      content: privacy,
      checked: privacyChecked,
      onChange: onPrivacyChange,
    },
    {
      key: 'thirdParty',
      label: '[필수] 개인정보 제3자 제공 동의',
      content: thirdParty,
      checked: thirdPartyChecked,
      onChange: onThirdPartyChange,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <i className="ri-shield-check-line text-[#2E7D6B]" />
        <span className="text-sm font-bold text-gray-900">약관 동의</span>
      </div>

      <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 mb-2">
        <p className="text-xs text-amber-800 leading-relaxed">
          서비스 이용을 위해 아래 약관에 동의해 주세요. 필수 항목에 동의하지 않으시면 등록이 제한됩니다.
        </p>
      </div>

      {items.map((item) => (
        <div key={item.key} className="space-y-2">
          <div className="flex items-center justify-between">
            <button
              onClick={() => !disabled && item.onChange(!item.checked)}
              disabled={disabled}
              className="flex items-center gap-3 cursor-pointer disabled:opacity-50"
            >
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-all ${
                  item.checked ? 'bg-[#2E7D6B]' : 'bg-gray-200'
                }`}
              >
                {item.checked && <i className="ri-check-line text-white text-xs" />}
              </div>
              <span className="text-sm text-gray-700">{item.label}</span>
            </button>
            <button
              onClick={() => setExpandedTerm(expandedTerm === item.key ? null : item.key)}
              className="text-xs text-gray-400 cursor-pointer flex items-center gap-0.5"
            >
              {expandedTerm === item.key ? '접기' : '보기'}
              <i className={`text-xs ${expandedTerm === item.key ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
            </button>
          </div>
          {expandedTerm === item.key && (
            <div className="ml-8 p-4 rounded-xl bg-gray-50 border border-gray-100 max-h-48 overflow-y-auto">
              <h4 className="text-sm font-bold text-gray-800 mb-2">{item.content.title}</h4>
              <p className="text-xs text-gray-500 whitespace-pre-line leading-relaxed">
                {item.content.content}
              </p>
            </div>
          )}
        </div>
      ))}

      {!privacyChecked || !thirdPartyChecked ? (
        <p className="text-xs text-red-400 text-center">필수 약관에 모두 동의해주세요.</p>
      ) : null}
    </div>
  );
}